import { track, logError } from '@tracking';
import getShop from '@core/reducers/shop/selectors/getShop';
import getCart from '@core/reducers/cart/selectors/getCart';
import getCartItems from '@core/reducers/cart/selectors/getCartItems';
import getCartTotal from '@core/reducers/cart/selectors/getCartTotal';
import getMenu from '@core/reducers/menu/selectors/getMenu';
import getFilterMenu from '@core/reducers/menu/selectors/getFilterMenu';
import getProduct from '@productConfig/selectors/getProduct';
import { getCountry, getSearchAsObject } from '@shared/selectors';
import { NOT_SET } from '@utils/tracking/constants';
import { QUERY_PARAM } from '@commons/constants';
import { MENU_SECTION } from '@conversion/constants';
import { getShopAreaId } from '@utils/shop';
import getShopName from '@core/reducers/shop/selectors/getShopName';
import getShopId from '@core/reducers/shop/selectors/getShopId';
import getShopTopCategories from '@core/reducers/shop/selectors/getShopTopCategories';
import { getCartStatus } from '../utils';

const {
  SEARCH,
  MENU_SECTION: MENU_SECTION_PARAM,
  FEATURED_PRODUCT_CONFIG,
} = QUERY_PARAM;

const EVENT_NAME = 'add_cart.clicked';

function getCategoryOfItem(menu, itemAdded) {
  try {
    return menu?.sections?.find(x =>
      x.products?.some(y => y.id === itemAdded.product),
    );
  } catch (error) {
    logError(EVENT_NAME, { message: 'Error on getCategoryOfItem' });
    return null;
  }
}

function getFeaturedProduct(search, shop) {
  const productId = (search && search[FEATURED_PRODUCT_CONFIG]) || null;
  if (productId) {
    const areaId = getShopAreaId(shop);
    return `${areaId || NOT_SET},${productId}`;
  }
  return NOT_SET;
}

const trackAddCartClicked = origin => (_, getState) => {
  try {
    const state = getState();
    const shop = getShop(state);
    const cart = getCart(state);
    const menu = getMenu(state);
    const search = getSearchAsObject(state);
    const filter = getFilterMenu(state);
    const country = getCountry(state);
    const cartItems = getCartItems(state);

    const itemAdded = cartItems[cartItems.length - 1];
    const category = getCategoryOfItem(menu, itemAdded);
    const product = getProduct(state);

    const shopId = getShopId(state);
    const shopName = getShopName(state);
    const cartValue = getCartTotal(state);
    const cartStatus = getCartStatus(cart);
    const menuSection =
      (search && search[MENU_SECTION_PARAM]) || MENU_SECTION.WIDGET;
    const productSku = itemAdded.product;
    const productQuantity = itemAdded.quantity;
    const productName = itemAdded.name;
    const productCategory =
      menuSection === MENU_SECTION.REPEAT_PRODUCT
        ? menuSection
        : category?.name || NOT_SET;
    const productCategoryIndex = category?.index || NOT_SET;
    const productHasPhoto = product?.image
      ? (product.hasCustomPhoto && 'custom_photo') || 'photo'
      : NOT_SET;
    const productIsMostOrdered = product?.rating === 10;
    const productSearched = (search && search[SEARCH]) || filter || NOT_SET;
    const featuredProduct = getFeaturedProduct(search, shop);
    const productUnitSalePrice = itemAdded.subtotal / itemAdded.quantity;
    const productUnitPrice = itemAdded.subtotalNoDiscount / itemAdded.quantity;
    const currencyCode = country.currencyIsoCode;
    const cuisineMain = getShopTopCategories(state);
    const productLimitQuantity = product?.maxQuantity;
    const eventOrigin = origin || 'menu';
    const screenType = NOT_SET;

    const payload = {
      shopId,
      shopName,
      cartValue,
      cartStatus,
      productSku,
      productQuantity,
      productName,
      productCategory,
      productCategoryIndex,
      productHasPhoto,
      productIsMostOrdered,
      productSearched,
      menuSection,
      featuredProduct,
      productUnitSalePrice,
      productUnitPrice,
      currencyCode,
      cuisineMain,
      productLimitQuantity,
      eventOrigin,
      screenType,
    };

    track(EVENT_NAME, payload);
  } catch (error) {
    logError(EVENT_NAME, error);
  }
};

export default trackAddCartClicked;
